import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { mtechnavi, sharelib } from '~/shared/libs/clientsdk';
import {
  Container,
  GetMessage,
  GetMessageWithIntl,
  LoadingIcon,
  MessageProps,
  Toast,
  success,
  error,
} from '~/shared/components';
import {
  ConfirmationDialog,
  DataFilterbox,
  DataFilterboxItem,
  PageNavigation,
  Textbox,
  Textarea,
  Filterbox,
  FileMemoDialogFormOption,
  FileUploadWithMemoDialog,
  FilterboxItem,
  Schema,
  getAltDisplaySchema,
  DateSuggest,
  ApprovalRequestDialog,
  Accordion,
} from '~/shared/components/ui';
import { SimpleListView } from '~/shared/components/ui/SimpleListView';
import { PresetItem } from '~/shared/services';
import { FilterExpression } from '~/worker';
import {
  FullMethodName_ListSharedContactContents,
  PageState,
  ViewId,
  getExceptionMessage,
  getWorkerExceptionMessage,
  saveLocalStorageCheckboxData,
  includeInputValidateError,
  autoDownloadFileOnlyName,
  autoBulkDownload,
  AttachmentItems,
  createAttachmentFilesWithCodes,
  RequireInput,
  formatUserWIthEmail,
  getPathnameByViewId,
} from '~/shared/utils';
import {
  convertDatetime,
  convertDate,
  convertUserReference,
  convertOrganizationStructureReference,
  convertOrganizationStructureReferenceToFilterboxItem,
  convertOrganizationToDataFilterBox,
} from '~/shared/utils/converter';
import { CaptionButton } from '~/shared/components/ui/Button/CaptionButton';
import './EstEstimateRequestInput.css';
import { SearchListViewDialog } from '~/shared/components/ui/SearchListViewDialog';
import { OutputOption } from '~/shared/components/ui/Dialog';
import { useAuth } from '~/shared/contexts/AuthProvider';
import {
  listSharedContactContents,
  listUsers,
  listEstimateRequestPlans,
  listEstimateManagements,
  listEstimateRequests,
  listEstimateRequestUnits,
} from '~/tenant/estimate/utils';
import Long from 'long';
import {
  FullMethodName_ListBusinessUnitBranchs,
  FullMethodName_ListUserAttributes,
} from '~/worker';

/**
 * 連絡先
 */
interface BusinessUnitContactViewItem {
  id: string; // レコードキー
  businessUnitContactId: string; // 連絡先ID
  businessUnitManagementId: string; // 取引先管理ID
  code: string; // 取引先コード
  displayName: string; // 取引先名（連絡先s）
  contactData?: mtechnavi.api.company.ISharedContactContent | null; // 対応する連絡先データ
}
/**
 * 添付ファイル
 */
interface AttachmentViewItem {
  id: string; // レコードキー
  displayName: string; // 添付ファイル
  attribute: string; // 備考
  attachment: AttachmentItems; // 添付ファイル（ダウンロード用）
}

const VIEW_ID: ViewId = 'EST_ESTIMATE_REQUEST_INPUT';

const MAX_REQUEST_UNIT = 10;
const MAX_ATTACHMENT = 10;

const businessUnitContactColumns = [
  {
    header: { id: 'businessUnitContact.code', viewId: VIEW_ID },
    propertyName: 'code',
    width: '8rem',
  },
  {
    header: { id: 'businessUnitContact.displayName', viewId: VIEW_ID },
    propertyName: 'displayName',
  },
];

const attachmentColumns = [
  {
    header: { id: 'attachment.displayName', viewId: VIEW_ID },
    propertyName: 'displayName',
    width: '15rem',
  },
  {
    header: { id: 'attachment.attribute', viewId: VIEW_ID },
    propertyName: 'attribute',
    readonly: true,
  },
];

export function EstEstimateRequestInput() {
  const sourcePageInfo = (useLocation().state as PageState) ?? [];
  const actionType = sourcePageInfo.actionType;
  const intl = useIntl();
  const myEmail = useAuth().user?.email ?? '';
  const [isLoading, setLoading] = useState(false);
  const [preset, setPreset] = useState<PresetItem>({ name: '' });
  const footerRef = useRef<HTMLDivElement>(null);
  const navi = useNavigate();
  const [backPageUrl, setBackPageUrl] = useState('');
  const loginUser = useAuth().user;
  const locationState = useLocation().state as PageState;

  const successMessage = GetMessage({ id: 'I0000001' });

  const viewMessageSave: MessageProps = {
    id: 'C0000001',
    value: {
      $1: GetMessageWithIntl(intl, { id: 'temporarySave' }),
    },
  };

  const viewMessageBack: MessageProps = {
    id: 'confirmationDialogMessageBack',
    prefixId: VIEW_ID,
  };

  const viewMessageBusinessUnitContactDelete: MessageProps = {
    id: 'confirmationDialogMessageBusinessUnitContactDelete',
    prefixId: VIEW_ID,
  };

  const viewMessageBusinessUnitContactDeleteAll: MessageProps = {
    id: 'confirmationDialogMessageBusinessUnitContactDeleteAll',
    prefixId: VIEW_ID,
  };

  const viewMessageAttachmentDelete: MessageProps = {
    id: 'confirmationDialogMessageAttachmentDelete',
    prefixId: VIEW_ID,
  };

  const viewMessageAttachmentDeleteAll: MessageProps = {
    id: 'confirmationDialogMessageAttachmentDeleteAll',
    prefixId: VIEW_ID,
  };

  // カラムの読み替え処理
  const onHandleFormatSchema = (schema: Schema) => {
    // ja表示のみする処理
    const jaColumn = ['displayNameLang'];
    return getAltDisplaySchema(schema, jaColumn, 'ja');
  };

  // 見積依頼予定ID
  const estimateRequestPlanIds = useMemo(() => {
    if (locationState.sourceViewId !== 'EST_ESTIMATE_REQUEST_PLAN_LIST') {
      return null;
    }
    return locationState.ids;
  }, [locationState]);

  // 確認ダイアログ
  const [isOpenConfirmDialog, setOpenConfirmDialog] = useState(false);
  // 連絡先検索ダイアログ
  const [
    isOpenBusinessUnitContactSearchDialog,
    setOpenBusinessUnitContactSearchDialog,
  ] = useState(false);
  // ファイル追加ダイアログ
  const [isOpenFileUploadDialog, setOpenFileUploadDialog] = useState(false);

  // 承認依頼ダイアログ
  const [isOpenApprovalRequestDialog, setOpenApprovalRequestDialog] =
    useState(false);

  // 確認ダイアログメッセージ
  const [confirmMessage, setConfirmMessage] =
    useState<MessageProps>(viewMessageBack);
  const confirmPromiseRef =
    useRef<(value: boolean | PromiseLike<boolean>) => void>();

  // 組織マスタ
  const organizations = useRef<mtechnavi.api.company.IOrganization[]>([]);
  // 組織所属情報
  const organizationRelations = useRef<
    mtechnavi.api.company.IOrganizationRelation[]
  >([]);
  // ユーザーマスタ
  const users = useRef<mtechnavi.api.tenantadmin.IUserAttribute[]>([]);
  // 連絡先コンテンツ
  const sharedContactContents = useRef<
    mtechnavi.api.company.ISharedContactContent[]
  >([]);
  // 拠点マスタ
  const businessUnitBranchs = useRef<
    mtechnavi.api.company.IBusinessUnitBranch[]
  >([]);

  // 画面アイテム
  const [trackingCode, setTrackingCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [estimateRequestAutoName, setEstimateRequestAutoName] = useState('');
  const [estimateDeadlineDt, setEstimateDeadlineDt] = useState<Date | null>();
  const [notice, setNotice] = useState('');
  const [contactOrganizationUnit, setContactOrganizationUnit] = useState<
    DataFilterboxItem[]
  >([]);
  const [contactStaff, setContactStaff] = useState<FilterboxItem[]>([]);
  const [systemNotificationUsers, setSystemNotificationUsers] = useState<
    DataFilterboxItem[]
  >([]);
  const [remarks, setRemarks] = useState<string>('');

  const [contactStaffCustomQuery, setContactStaffCustomQuery] =
    useState<FilterExpression>({});

  // 組織マスタ
  const [organizationList, setOrganizationList] =
    useState<mtechnavi.api.company.IOrganization[]>();

  // 自社企業情報
  const myCompanyInfo = useRef<mtechnavi.api.company.ICompany>();

  // 取得データ格納用 見積管理データ
  const estimateManagementData =
    useRef<mtechnavi.api.estimation.IEstimateManagement>({});
  // 取得データ格納用 見積依頼データ
  const estimateRequestData = useRef<mtechnavi.api.estimation.IEstimateRequest>(
    {}
  );
  // 取得データ格納用 見積依頼送信先データ
  const estimateRequestUnitData = useRef<
    mtechnavi.api.estimation.IEstimateRequestUnit[]
  >([]);

  // 入力チェック用
  const requiredDisplayNameArea = useRef(null);

  // 連絡先リスト (表示用)
  const [businessUnitContactViewItem, setBusinessUnitContactViewItem] =
    useState<BusinessUnitContactViewItem[]>([]);

  // 添付ファイルリスト (表示用)
  const [attachmentViewItem, setAttachmentViewItem] = useState<
    AttachmentViewItem[]
  >([]);

  // filterbox用(問合せ組織)
  const managementItemType = {
    value: 'organizationId',
    displayName: 'displayName',
  };
  // filterbox用(担当者)
  const staffItemType = {
    value: 'user.email',
    displayName: 'user.displayName',
  };

  // DataFilterbox用(依頼管理組織)
  const organizationTosDataFilterItems = useMemo(
    () =>
      organizationList
        ? convertOrganizationToDataFilterBox(organizationList)
        : [],
    [organizationList]
  );

  // DataFilterbox用(システム通知先)
  const systemNotificationUsersList = useMemo(
    () =>
      users.current
        ? users.current!.map(
            (v): FilterboxItem => ({
              displayName: formatUserWIthEmail(
                v.user?.displayName,
                v.user?.email
              ),
              value: v.user?.userId ?? '',
            })
          ) ?? []
        : [],
    // 想定通りの挙動なのでlintから除外させる
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users.current]
  );

  /**
   * 連絡先検索ダイアログ選択時処理
   */
  const handleSearchSelect = (
    contacts: mtechnavi.api.company.ISharedContactContent[]
  ) => {
    if (contacts.length === 0) {
      return;
    }

    if (
      contacts &&
      contacts.length + businessUnitContactViewItem.length > MAX_REQUEST_UNIT
    ) {
      error([
        GetMessageWithIntl(intl, {
          id: 'E0000138',
          value: { $1: MAX_REQUEST_UNIT },
        }),
      ]);
      return;
    }

    const listViewItems: BusinessUnitContactViewItem[] = [];
    contacts.forEach((contact) => {
      const duplicateId = businessUnitContactViewItem.some(
        (w) =>
          w.businessUnitContactId ===
          contact.businessUnitContact?.businessUnitContactId
      );
      if (duplicateId) {
        return;
      }

      // 表示用に加工して追加
      listViewItems.push({
        id: contact.businessUnitContact?.businessUnitContactId ?? '',
        businessUnitContactId:
          contact.businessUnitContact?.businessUnitContactId ?? '',
        businessUnitManagementId:
          contact.businessUnitManagement?.businessUnitManagementId ?? '',

        code: contact.businessUnitManagement?.code ?? '',
        displayName: contact.displayName ?? '',
        contactData: contact,
      });
    });

    setBusinessUnitContactViewItem(
      sortBusinessUnitContact([
        ...businessUnitContactViewItem,
        ...listViewItems,
      ])
    );

    clearCheckBox();
    setOpenBusinessUnitContactSearchDialog(false);
  };

  const clearCheckBox = useCallback(() => {
    saveLocalStorageCheckboxData('SHARED_CONTACT_CONTENTS_SEARCH', [], myEmail);
  }, [myEmail]);

  // 画面データ取得
  useEffect(() => {
    setLoading(true);

    //非表示項目リセット
    estimateManagementData.current = {};
    estimateRequestData.current = {};
    estimateRequestUnitData.current = [];

    // 戻るボタンURLセット
    setBackPageUrl(
      getPathnameByViewId(sourcePageInfo.sourceViewId)?.path ?? ''
    );

    (async () => {
      try {
        // 自社情報を取得
        myCompanyInfo.current =
          await window.App.services.companyService.getCompany({});
        // 共通で取得するもの
        const [
          { presetItem },
          listOrganizationRes,
          listUsableOrganizationRes,
          listOrganizationRelationRes,
          listSharedContactContentsRes,
          listUsersRes,
          listBusinessUnitBranchsRes,
        ] = await Promise.all([
          window.App.services.ui.getViewIdPreset(VIEW_ID), // プリセット取得
          window.App.services.ui.listOrganization(), // 組織リスト
          window.App.services.ui.listOrganization(false), // 組織リスト
          window.App.services.ui.listOrganizationRelation(), // 組織所属情報
          listSharedContactContents(), // 連絡先コンテンツ
          listUsers(), // ユーザーマスタ
          listBusinessUnitBranchs(), // 拠点マスタ
        ]);
        setPreset(presetItem);
        setOrganizationList(listUsableOrganizationRes || []);
        organizations.current = listOrganizationRes || [];
        organizationRelations.current = listOrganizationRelationRes || [];
        sharedContactContents.current =
          listSharedContactContentsRes.items || [];
        users.current = listUsersRes || [];
        businessUnitBranchs.current = listBusinessUnitBranchsRes.items || [];

        // 新規登録モード（見積依頼一覧から追加）
        if (actionType === 'add') {
          userToViews(loginUser);
        }
        // 見積依頼予定一覧から見積依頼予定を選択して登録
        if (
          actionType === 'add' &&
          sourcePageInfo.sourceViewId === 'EST_ESTIMATE_REQUEST_PLAN_LIST'
        ) {
          if (!estimateRequestPlanIds) return;
          // 見積依頼予定から取得
          const [listEstimateRequestPlansRes] = await Promise.all([
            listEstimateRequestPlans(estimateRequestPlanIds ?? [], false),
          ]);
          const plans: mtechnavi.api.estimation.IEstimateRequestPlan[] = [];

          const firstPlan = listEstimateRequestPlansRes.items.find(
            (v) => v.estimateRequestPlanId === estimateRequestPlanIds[0]
          );
          const otherPlan = listEstimateRequestPlansRes.items.filter(
            (v) => v.estimateRequestPlanId !== estimateRequestPlanIds[0]
          );

          if (firstPlan) {
            plans.push(firstPlan);
          }
          if (otherPlan.length) {
            plans.push(...otherPlan);
          }

          // 画面項目のセット
          estimatePlanToViews(estimateRequestPlanIds[0], plans);
          userToViews(loginUser);
        }
        // 編集モード（見積依頼確認から遷移）
        if (actionType === 'edit') {
          const [
            listEstimateManagementsRes,
            listEstimateRequestsRes,
            // listEstimateRequestDetailsRes,
            listEstimateRequestUnitsRes,
          ] = await Promise.all([
            listEstimateManagements(sourcePageInfo.ids ?? [], false),
            listEstimateRequests(sourcePageInfo.ids ?? [], false),
            listEstimateRequestUnits(sourcePageInfo.ids ?? [], false),
          ]);

          // 非表示項目のセット
          estimateManagementData.current =
            listEstimateManagementsRes.items.at(0) || {};
          estimateRequestData.current =
            listEstimateRequestsRes.items.at(0) || {};
          estimateRequestUnitData.current = listEstimateRequestUnitsRes.items;

          // 画面項目のセット
          estimateDataToViews(
            estimateManagementData.current,
            estimateRequestData.current,
            estimateRequestUnitData.current
          );
        }
        // 複写モード
        if (actionType === 'copy') {
          const [listEstimateRequestsRes] = await Promise.all([
            listEstimateRequests(sourcePageInfo.ids ?? [], true),
          ]);

          // 非表示項目のセット
          estimateRequestData.current =
            listEstimateRequestsRes.items.at(0) || {};

          // 複写処理のみクリア処理
          clearItemForCopy();

          // 画面項目のセット
          estimateDataToViews(
            estimateManagementData.current,
            estimateRequestData.current,
            estimateRequestUnitData.current
          );
          userToViews(loginUser);
        }
      } catch (err) {
        error(getExceptionMessage(intl, err));
      } finally {
        setLoading(false);
      }
    })();

    // 初回だけ起動させたい処理なのでlintから除外させる
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listBusinessUnitBranchs = async () => {
    const listRes = (await window.App.services.ui.worker.filter({
      action: 'reload',
      fullMethodName: FullMethodName_ListBusinessUnitBranchs,
      filter: {},
      requestBody: {
        companyIds: [myCompanyInfo.current?.companyId as string],
      },
      sort: [],
    })) as mtechnavi.api.company.ListBusinessUnitBranchsResponse;

    return new Promise<mtechnavi.api.company.ListBusinessUnitBranchsResponse>(
      (resolve, reject) => {
        const firstItem = listRes?.items.length > 0 ? listRes?.items[0] : {};
        if (firstItem?.businessUnitBranchId === '') {
          reject({ id: 'E0000070' });
          return;
        }
        resolve(listRes);
      }
    );
  };

  /**
   * 複写時にクリアする項目のセット処理
   */
  const clearItemForCopy = () => {
    estimateRequestData.current = {
      ...estimateRequestData.current,
      estimateRequestId: null,
      estimateRequestAutoName: null,
      trackingCode: null,
      estimateDeadlineDt: null,
      desiredReceiveType: null,
      desiredReceiveDt: null,
      desiredReceiveRemarks: null,
      orderPlanDt: null,
      orderPlanRemarks: null,
      contactOrganization: null,
      contactStaff: null,
      sendedAt: null,
      sharedProperties: null,
      createdProperties: null,
      updatedProperties: null,
      createdAt: null,
      updatedAt: null,
      deletedAt: null,
    };
  };

  /**
   * ユーザー情報からの画面データセット
   * 新規時のみ使用する。
   */
  const userToViews = (user: mtechnavi.api.idp.IUser | undefined) => {
    if (!user) return;

    const initialStaff = users.current.find(
      (v) => v?.user?.email === loginUser?.email
    );

    const filterboxItemContactStaff = initialStaff
      ? [
          {
            value: initialStaff.user?.email ?? '',
            displayName: initialStaff.user?.displayName ?? '',
          },
        ]
      : [];
    setContactStaff(filterboxItemContactStaff);

    (async () => {
      const userRepresentativeOrg =
        await window.App.services.ui.getUserRepresentativeOrganization(
          loginUser?.email
        );
      const managementOrg =
        convertOrganizationStructureReferenceToFilterboxItem(
          userRepresentativeOrg
        );
      if (managementOrg) {
        setContactOrganizationUnit([managementOrg]);
      }
    })();

    const usersFilterBoxItems: FilterboxItem[] = [
      {
        displayName: formatUserWIthEmail(user.displayName, user.email),
        value: user.userId ?? '',
      },
    ];
    setSystemNotificationUsers(usersFilterBoxItems);
  };

  /**
   * 見積依頼予定からの画面データセット
   * 新規時のみ使用する。
   */
  const estimatePlanToViews = (
    mainEstimateRequestPlanId: string,
    estimateRequestPlans: mtechnavi.api.estimation.IEstimateRequestPlan[]
  ) => {
    if (!estimateRequestPlans.length) return;

    const mainEstimateRequestPlan = estimateRequestPlans.find(
      (v) => v.estimateRequestPlanId === mainEstimateRequestPlanId
    );

    setTrackingCode(mainEstimateRequestPlan?.sapWorkOrder?.trackingCode ?? '');
    setDisplayName(mainEstimateRequestPlan?.displayNameLang?.ja ?? '');
  };

  /**
   * 見積関連データからの画面データセット
   */
  const estimateDataToViews = (
    management: mtechnavi.api.estimation.IEstimateManagement,
    request: mtechnavi.api.estimation.IEstimateRequest,
    units: mtechnavi.api.estimation.IEstimateRequestUnit[]
  ) => {
    estimateManagementToViews(management);
    estimateRequestToViews(request);
    estimateRequestUnitsToViews(units);
  };

  /**
   * 見積管理データからの画面データセット
   */
  const estimateManagementToViews = (
    management: mtechnavi.api.estimation.IEstimateManagement
  ) => {
    const usersFilterBoxItems: FilterboxItem[] = (
      management.systemNotificationUsers || []
    ).map((v) => ({
      displayName: formatUserWIthEmail(v.displayName, v.email),
      value: v.userId ?? '',
    }));
    setSystemNotificationUsers(usersFilterBoxItems);
    setRemarks(management.remarks ?? '');
  };

  /**
   * 見積依頼データからの画面データセット
   */
  const estimateRequestToViews = (
    request: mtechnavi.api.estimation.IEstimateRequest
  ) => {
    setTrackingCode(request.trackingCode ?? '');
    setDisplayName(request.displayName ?? '');
    setEstimateRequestAutoName(
      request.estimateRequestAutoName
        ? Long.fromValue(request.estimateRequestAutoName).toString(10)
        : ''
    );
    const estimateDeadlineDate = convertDate(
      request.estimateDeadlineDt || null
    );
    setEstimateDeadlineDt(estimateDeadlineDate);
    setNotice(request.notice ?? '');

    const filterboxItemContactOrganizationUnit =
      convertOrganizationStructureReferenceToFilterboxItem(
        request.contactOrganization
      );
    setContactOrganizationUnit(
      filterboxItemContactOrganizationUnit
        ? [filterboxItemContactOrganizationUnit]
        : []
    );
    const filterboxItemContactStaff = request.contactStaff
      ? [
          {
            value: request.contactStaff?.email ?? '',
            displayName: request.contactStaff?.displayName ?? '',
          },
        ]
      : [];
    setContactStaff(filterboxItemContactStaff);

    // 添付ファイル
    const newList: AttachmentViewItem[] = [];
    request.attachments?.forEach((v) => {
      newList.push({
        id: v.assetId ?? '',
        displayName: v.filename ?? '',
        attribute: v.remarks ?? '',
        attachment: v,
      } as AttachmentViewItem);
    });
    setAttachmentViewItem(newList);
  };

  /**
   * 見積依頼送信先データからの画面データセット
   */
  const estimateRequestUnitsToViews = (
    estimateRequestUnints: mtechnavi.api.estimation.IEstimateRequestUnit[]
  ) => {
    if (!estimateRequestUnints) return;

    // 取引先を連絡先単位に展開して保持する
    const listViewItems = estimateRequestUnints?.flatMap((v) =>
      (v.businessUnitContactIds || []).map(
        (contactId): BusinessUnitContactViewItem => {
          const contactData = sharedContactContents.current.find(
            (item) =>
              item.businessUnitContact?.businessUnitContactId === contactId
          );
          return {
            id: contactId ?? '',
            businessUnitContactId: contactId ?? '',
            businessUnitManagementId: v.businessUnitManagementId ?? '',
            code: v.code ?? '',
            displayName: contactData?.displayName ?? '',
            contactData: contactData,
          };
        }
      )
    );
    setBusinessUnitContactViewItem(sortBusinessUnitContact(listViewItems));
  };

  /**
   * 依頼送信先のソート
   */
  const sortBusinessUnitContact = (data: BusinessUnitContactViewItem[]) => {
    return data.sort((val1, val2) => {
      return val1.code.localeCompare(val2.code, 'ja');
    });
  };

  // 表示用の連絡先リストから保存用の取引先リストに変換する
  const contactListToRequestUnits = (
    items: BusinessUnitContactViewItem[]
  ): mtechnavi.api.estimation.IEstimateRequestUnit[] => {
    const resultList: mtechnavi.api.estimation.IEstimateRequestUnit[] = [];
    items.forEach(
      ({ businessUnitContactId, businessUnitManagementId, contactData }) =>
        contactItemToRequestUnit(
          resultList,
          businessUnitContactId,
          businessUnitManagementId,
          contactData
        )
    );

    return resultList;
  };

  const contactItemToRequestUnit = (
    workingResultList: mtechnavi.api.estimation.IEstimateRequestUnit[],
    businessUnitContactId: string,
    businessUnitManagementId: string,
    contactData?: mtechnavi.api.company.ISharedContactContent | null
  ) => {
    const duplicateRequestUnit = workingResultList.find(
      (item) => item.businessUnitManagementId === businessUnitManagementId
    );

    if (duplicateRequestUnit) {
      // 重複した取引先
      // 連絡先IDも重複していたらスキップ
      if (
        duplicateRequestUnit.businessUnitContactIds?.includes(
          businessUnitContactId
        )
      ) {
        return;
      }
      // 重複していない連絡先IDを追加
      duplicateRequestUnit.businessUnitContactIds = [
        ...(duplicateRequestUnit.businessUnitContactIds || []),
        businessUnitContactId,
      ];
      // 連絡先ユーザを追加
      contactData?.businessUnitContact?.notificationUsers?.forEach(
        (notifyUser) => {
          if (!notifyUser.user) {
            return;
          }
          if (
            duplicateRequestUnit.contactUsers?.every(
              (contact) => contact.email !== notifyUser.user?.email
            )
          ) {
            duplicateRequestUnit.contactUsers.push(notifyUser.user);
          }
        }
      );
    } else {
      // 重複していない取引先
      const requestUnit: mtechnavi.api.estimation.IEstimateRequestUnit = {
        businessUnitManagementId:
          contactData?.businessUnitManagement?.businessUnitManagementId ?? '',
        businessUnitContactIds: [
          contactData?.businessUnitContact?.businessUnitContactId || '',
        ],
        companyId: contactData?.businessUnitManagement?.companyId ?? '',
        code: contactData?.businessUnitManagement?.code ?? '',
        displayName: contactData?.businessUnitManagement?.displayName ?? '',
        contactUsers: (
          contactData?.businessUnitContact?.notificationUsers || []
        ).map((notifyUser) => notifyUser.user || {}),
      };
      workingResultList.push(requestUnit);
    }
  };

  // データ保存時の入力項目エラーチェック
  const isInputErrorCheck = () => {
    const requiredItem: RequireInput[] = [];
    requiredItem.push({
      value: displayName ?? '',
      ref: requiredDisplayNameArea,
    });

    // 入力エラーチェック
    const targetEle = document.querySelector('.scroll-main-contents-area');
    if (includeInputValidateError(targetEle, intl, requiredItem)) {
      return true;
    }
    return false;
  };

  // 依頼明細入力チェック(承認依頼時)
  const isRequestDetailApprovalRequestErrorCheck = () => {
    // 見積依頼先未選択チェック
    if (businessUnitContactViewItem.length < 1) {
      error([GetMessageWithIntl(intl, { id: 'E0000084' })]);
      return true;
    }

    return false;
  };

  // データの永続化
  const saveAction = async () => {
    // システム通知先
    const selectedSystemNotificationUsers: sharelib.IUserReference[] = [];
    systemNotificationUsers.forEach((v) => {
      const selectedUser = (users.current ?? []).find(
        (w) => w.userAttributeId === v.value
      );
      if (selectedUser) {
        selectedSystemNotificationUsers.push({
          userId: selectedUser.user?.userId,
          email: selectedUser.user?.email,
          displayName: selectedUser.user?.displayName,
        });
      }
    });

    const selectedContactOrganizationUnit = (organizations.current ?? []).find(
      (v) => v.organizationId === contactOrganizationUnit.at(0)?.value
    );
    const selectedContactStaff = (users.current ?? []).find(
      (v) => v.user?.email === contactStaff.at(0)?.value
    );

    const reqEstimateManagement: mtechnavi.api.estimation.IEstimateManagement =
      {
        estimateManagementId:
          estimateManagementData.current.estimateManagementId,
        systemNotificationUsers: selectedSystemNotificationUsers,
        remarks: remarks,
        status: null,
        approvalStatus: null,
        approvalRequest: null,
        approvalResult: null,
        issuer: null,
        createdAt: estimateManagementData.current.createdAt,
        updatedAt: estimateManagementData.current.updatedAt,
        deletedAt: null,
      };

    const reqEstimateRequest: mtechnavi.api.estimation.IEstimateRequest = {
      estimateRequestId: estimateRequestData.current.estimateRequestId,
      estimateRequestAutoName:
        estimateRequestData.current.estimateRequestAutoName,
      trackingCode: trackingCode,
      displayName: displayName,
      estimateDeadlineDt: convertDatetime(
        estimateDeadlineDt || null,
        'YYYY/MM/DD'
      ),
      desiredReceiveType: null,
      desiredReceiveDt: null,
      desiredReceiveRemarks: null,
      orderPlanDt: null,
      orderPlanRemarks: null,
      topPriorityContent: null,
      purpose: null,
      notice: notice,
      deliveryPointType: null,
      deliveryPoint: null,
      deliveryAddress: null,
      deliveryPhoneNumber: null,
      deliveryPlace: null,
      deliveryImage: null,
      deliveryAttachment: null,
      contactOrganization: await convertOrganizationStructureReference(
        selectedContactOrganizationUnit?.organizationId ?? ''
      ),
      contactStaff: await convertUserReference(
        selectedContactStaff?.user?.email ?? ''
      ),
      requesterDepartment: null,
      requesterResponsible: null,
      attachments: attachmentViewItem.map((v) => v.attachment),
      requestingCompany: null,
      sendedAt: null,
      sharedProperties: null,
      createdProperties: null,
      updatedProperties: null,
      createdAt: estimateRequestData.current.createdAt,
      updatedAt: estimateRequestData.current.updatedAt,
      deletedAt: null,
    };
    const reqEstimateRequestDetails: mtechnavi.api.estimation.IEstimateRequestDetail[] =
      [];

    const reqEstimateRequestUnits: mtechnavi.api.estimation.IEstimateRequestUnit[] =
      contactListToRequestUnits(businessUnitContactViewItem);

    const requestData = {
      estimateManagement: reqEstimateManagement,
      estimateRequest: reqEstimateRequest,
      estimateRequestDetails: reqEstimateRequestDetails,
      estimateRequestUnits: reqEstimateRequestUnits,
    };

    const res = (await window.App.services.ui.worker.apiCall({
      actionName: 'saveEstimateManagementWithRelatedItems',
      request: requestData,
    })) as mtechnavi.api.estimation.ISaveEstimateManagementWithRelatedItemsResponse;

    return res;
  };

  // 画面情報の再取得
  const reloadDisplay = async (estimateManagementId: string) => {
    if (!estimateManagementId) return;
    const [
      listEstimateManagementsRes,
      listEstimateRequestsRes,
      listEstimateRequestUnitsRes,
    ] = await Promise.all([
      listEstimateManagements([estimateManagementId], true),
      listEstimateRequests([estimateManagementId], true),
      listEstimateRequestUnits([estimateManagementId], true),
    ]);

    // 非表示項目のセット
    estimateManagementData.current =
      listEstimateManagementsRes.items.at(0) || {};
    estimateRequestData.current = listEstimateRequestsRes.items.at(0) || {};
    estimateRequestUnitData.current = listEstimateRequestUnitsRes.items || [];

    // 画面項目のセット
    estimateDataToViews(
      estimateManagementData.current,
      estimateRequestData.current,
      estimateRequestUnitData.current
    );
  };

  // 一時保存
  const handleSave = async () => {
    // 入力エラーチェック
    if (isInputErrorCheck()) {
      return;
    }
    // 確認処理
    if (!(await confirmation(viewMessageSave))) {
      return;
    }
    setLoading(true);
    try {
      const res =
        (await saveAction()) as mtechnavi.api.estimation.ISaveEstimateManagementWithRelatedItemsResponse[];
      estimateManagementData.current = res.at(0)?.estimateManagement ?? {};
      success([successMessage]);
      setOpenConfirmDialog(false);
    } catch (err) {
      error(getWorkerExceptionMessage(intl, err));
      throw err;
    } finally {
      // reload処理 画面情報の再取得
      await reloadDisplay(
        estimateManagementData.current.estimateManagementId ?? ''
      );
      setLoading(false);
    }
  };

  const handleApprovalRequest = async (result: OutputOption) => {
    setLoading(true);
    try {
      // 保存処理
      const res =
        (await saveAction()) as mtechnavi.api.estimation.ISaveEstimateManagementWithRelatedItemsResponse[];
      // 選択値を担当者情報に変換
      const selectedStaff =
        (await convertUserReference(
          result?.approvalOrganizationStaff[0]?.value || ''
        )) || {};

      // 管理場所マスタから取得
      // 選択値を管理場所情報に変換
      const selectedOrganization =
        (await convertOrganizationStructureReference(
          result?.approvalOrganizationUnit[0]?.value || ''
        )) || {};

      await window.App.services.ui.worker.apiCall({
        actionName: 'approvalRequestEstimateManagement',
        request: {
          estimateManagementId:
            res.length > 0
              ? res[0].estimateManagement?.estimateManagementId ?? ''
              : '',
          approvalPlanStaff: res.length > 0 ? selectedStaff : {},
          approvalPlanOrganization: res.length > 0 ? selectedOrganization : {},
          comment: result.comment,
        },
      });

      success([successMessage]);
      setOpenApprovalRequestDialog(false);
    } catch (err) {
      error(getWorkerExceptionMessage(intl, err));
      throw err;
    } finally {
      setLoading(false);
    }
    // 一覧に戻る
    backToBaseViewPage();
  };

  /**
   * 見積依頼先の削除
   */
  const handleDeleteBusinessUnitContact = async (
    item: BusinessUnitContactViewItem
  ) => {
    if (!(await confirmation(viewMessageBusinessUnitContactDelete))) {
      return;
    }
    setBusinessUnitContactViewItem(
      businessUnitContactViewItem?.filter((v) => v.id !== item.id)
    );
  };

  /**
   * 見積依頼先の全削除
   */
  const handleAllDeleteBusinessUnitContact = async () => {
    if (!(await confirmation(viewMessageBusinessUnitContactDeleteAll))) {
      return;
    }
    setBusinessUnitContactViewItem([]);
  };

  /**
   * 添付ファイルの追加
   */
  const handleAddAttachment = async (result: FileMemoDialogFormOption) => {
    if (!result.files) return;
    if (
      attachmentViewItem &&
      attachmentViewItem.length + result.files?.length > MAX_ATTACHMENT
    ) {
      error([
        GetMessageWithIntl(intl, {
          id: 'E0000077',
          value: { $1: MAX_ATTACHMENT },
        }),
      ]);
      return;
    }
    setLoading(true);

    const files = result.files.map((file) => ({
      id: `${file.filename ?? ''}`,
      category: {},
      assetId: '',
      filename: `${file.filename ?? ''}`,
      url: file.url,
      mimeType: '',
      remarks: '',
      linkType: {},
    }));

    try {
      const tmpData = await createAttachmentFilesWithCodes(
        files,
        'B11',
        'B02',
        'B01'
      );
      if (tmpData.length > 0) {
        const newList: AttachmentViewItem[] = [];
        tmpData.forEach((v) => {
          newList.push({
            id: v.assetId,
            displayName: v.filename,
            attribute: result.memo,
            attachment: { ...v, remarks: result.memo ?? '' },
          } as AttachmentViewItem);
        });

        setAttachmentViewItem([...attachmentViewItem, ...newList]);
      } else {
        setAttachmentViewItem([...attachmentViewItem]);
      }
    } catch (err) {
      error(getWorkerExceptionMessage(intl, err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * 添付ファイルの削除
   */
  const handleDeleteAttachment = async (item: AttachmentViewItem) => {
    if (!(await confirmation(viewMessageAttachmentDelete))) {
      return;
    }
    setAttachmentViewItem(attachmentViewItem?.filter((v) => v.id !== item.id));
  };

  /**
   * 添付ファイルの全削除
   */
  const handleAllDeleteAttachment = async () => {
    if (!(await confirmation(viewMessageAttachmentDeleteAll))) {
      return;
    }
    setAttachmentViewItem([]);
  };

  /**
   * 添付ファイル一覧の行クリック
   */
  const handleRowClickAttachment = (item: AttachmentViewItem) => {
    autoDownloadFileOnlyName(
      item.attachment.filename ?? '',
      item.attachment.assetId ?? ''
    );
  };

  /**
   * 添付ファイル一覧の全件ダウンロード
   */
  const handleFullDownloadAttachment = () => {
    autoBulkDownload(
      (attachmentViewItem ?? []).map((item) => item.attachment.assetId ?? ''),
      intl,
      VIEW_ID
    );
  };

  // 問合せ部門変更時
  useEffect(() => {
    if (contactOrganizationUnit.length === 0) {
      setContactStaffCustomQuery({});
      return;
    }
    const users = organizationRelations.current.filter(
      (item) => contactOrganizationUnit[0].value === item.organizationId
    );
    const query: FilterExpression = users
      ? {
          'user.userId': {
            $in:
              users.map((item) => {
                return item.userId ?? '';
              }) ?? [],
          },
        }
      : {};
    setContactStaffCustomQuery(query);
  }, [contactOrganizationUnit]);

  useEffect(() => {
    if (contactStaff.length > 0) {
      const selectedStaff = users.current.find(
        (v) => v.user?.email === contactStaff[0].value
      );
      const representativeOrg = selectedStaff?.representativeOrganization;

      if (
        contactOrganizationUnit.length === 0 &&
        representativeOrg?.organizationId
      ) {
        const managementOrg =
          convertOrganizationStructureReferenceToFilterboxItem(
            representativeOrg
          );
        // 担当者の所属組織が依頼管理組織に存在するか確認
        const setItem = organizations.current.filter(
          (item) => item.organizationId === representativeOrg.organizationId
        );
        // 担当者の所属組織が存在する場合
        if (setItem.length > 0) {
          setContactOrganizationUnit(managementOrg ? [managementOrg] : []);
        } else {
          setContactOrganizationUnit([]);
        }

        return;
      }
    }

    // contactStaff 変更時だけ起動させたい処理なのでlintから除外させる
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactStaff]);

  /**
   * 確認ダイアログ処理
   * Promise で結果を制御する。
   * 確定: true / キャンセル: false
   */
  const confirmation = (viewMessage: MessageProps): Promise<boolean> => {
    setOpenConfirmDialog(true);
    setConfirmMessage(viewMessage);
    return new Promise((resolve) => {
      confirmPromiseRef.current = resolve;
    });
  };

  /**
   * 確認ダイアログの確認処理
   */
  const handleConfirmed = () => {
    if (confirmPromiseRef.current) {
      confirmPromiseRef.current(true);
    }
    setOpenConfirmDialog(false);
  };

  /**
   * 確認ダイアログのキャンセル処理
   */
  const handleCancel = () => {
    if (confirmPromiseRef.current) {
      confirmPromiseRef.current(false);
    }
    setOpenConfirmDialog(false);
  };

  // 戻るページ
  const backToPreviewPage = async () => {
    if (!(await confirmation(viewMessageBack))) {
      return;
    }
    const state: PageState = {
      ids: sourcePageInfo.beforeStateIds ?? [],
      sourceViewId: VIEW_ID,
      naviFilters: sourcePageInfo.naviFilters,
      beforeStateIds: sourcePageInfo.beforeStateIds,
      baseViewOption: sourcePageInfo.baseViewOption,
      confirmationViewOption: sourcePageInfo.confirmationViewOption,
    };
    navi(backPageUrl, { state });
  };

  // 一覧に戻る
  const backToBaseViewPage = () => {
    const state: PageState = {
      ids: sourcePageInfo.beforeStateIds ?? [],
      sourceViewId: VIEW_ID,
      naviFilters: sourcePageInfo.naviFilters,
      beforeStateIds: sourcePageInfo.beforeStateIds,
      baseViewOption: sourcePageInfo.baseViewOption,
    };

    // 戻り先に遷移元(一覧)情報をセット
    //  遷移先不明の場合は見積依頼一覧に戻る
    const backPath =
      getPathnameByViewId(sourcePageInfo.baseViewOption?.sourceViewId)?.path ??
      '/estimate/est-estimate-request-list';

    navi(backPath, { state });
  };

  return (
    <>
      <Container viewId={VIEW_ID}>
        <div className="EstEstimateRequestInput">
          <div className="header">
            <PageNavigation
              backpagePath={backPageUrl}
              pageInfo={{
                isVisibleMoveNavi: false,
              }}
              infoOption={{
                lastUpdateInfo: { isVisibleUpdateInfo: false },
                issuerInfo: { isVisibleIssuerInfo: false },
              }}
              handleBackPage={backToPreviewPage}
            />
          </div>
          <div className="scroll-main-contents-area">
            {/* 公開情報（基本情報） */}
            <Accordion title={GetMessage({ id: 'request', prefixId: VIEW_ID })}>
              <div className="input-line">
                <div className="item-group-100">
                  <div className="w-40" ref={requiredDisplayNameArea}>
                    <Textbox
                      name="displayName"
                      className="field"
                      value={displayName}
                      type="text"
                      labelId="EST_ESTIMATE_REQUEST_INPUT.displayName"
                      columns={preset.columns}
                      onChangeState={setDisplayName}
                      validateOption={{ required: true }}
                    />
                  </div>
                  <div className="w-20">
                    <Textbox
                      name="trackingCode"
                      className="field"
                      value={trackingCode}
                      type="text"
                      labelId="EST_ESTIMATE_REQUEST_INPUT.trackingCode"
                      columns={preset.columns}
                      onChangeState={setTrackingCode}
                    />
                  </div>
                  <div className="w-20">
                    <Textbox
                      name="estimateRequestAutoName"
                      className="field"
                      value={estimateRequestAutoName}
                      type="text"
                      labelId="EST_ESTIMATE_REQUEST_INPUT.estimateRequestAutoName"
                      columns={preset.columns}
                      disabled={true}
                    />
                  </div>
                </div>
              </div>
              <div className="input-line">
                <div className="item-group-100">
                  <div className="w-20">
                    <DateSuggest
                      labelId="EST_ESTIMATE_REQUEST_INPUT.estimateDeadlineDt"
                      name="estimateDeadlineDt"
                      columns={preset.columns}
                      value={estimateDeadlineDt}
                      onChangeState={setEstimateDeadlineDt}
                    ></DateSuggest>
                  </div>
                </div>
              </div>
              <div className="input-line">
                <div className="item-group-100">
                  <div className="w-60">
                    <Textarea
                      labelId="EST_ESTIMATE_REQUEST_INPUT.notice"
                      name="notice"
                      className="w-100 mh-middle"
                      value={notice}
                      columns={preset.columns}
                      onChangeState={setNotice}
                    />
                  </div>
                </div>
              </div>
              <div className="input-line">
                <div className="item-group-100">
                  <div className="w-40">
                    <DataFilterbox
                      data={organizationTosDataFilterItems}
                      name="contactOrganizationUnit"
                      labelId="EST_ESTIMATE_REQUEST_INPUT.contactOrganizationUnit"
                      columns={['contactOrganizationUnit']}
                      searchOption={{ targets: 'displayName' }}
                      onChangeState={(v) => {
                        setContactStaff([]);
                        setContactOrganizationUnit(v);
                      }}
                      value={contactOrganizationUnit}
                    />
                  </div>
                  <div className="w-20">
                    <Filterbox
                      name="contactStaff"
                      fullMethodName={FullMethodName_ListUserAttributes}
                      labelId="EST_ESTIMATE_REQUEST_INPUT.contactStaff"
                      columns={['contactStaff']}
                      value={contactStaff ?? [{ value: '', displayName: '' }]}
                      itemType={staffItemType}
                      searchOption={{
                        targets: 'displayName',
                        customQuery: contactStaffCustomQuery,
                      }}
                      onChangeState={setContactStaff}
                    />
                  </div>
                </div>
              </div>
            </Accordion>

            {/* 添付ファイル */}
            <Accordion
              title={GetMessage({ id: 'attachment', prefixId: VIEW_ID })}
            >
              {/* 添付ファイル */}
              <div className="input-line">
                <div className="item-group-100 file-area">
                  <div className="w-50">
                    <SimpleListView
                      data={attachmentViewItem}
                      viewOptions={{
                        previewRowCount: 5,
                        columns: attachmentColumns,
                      }}
                      actionOptions={{
                        onDelete: handleDeleteAttachment,
                        onDeleteAll: handleAllDeleteAttachment,
                        onRowClick: handleRowClickAttachment,
                        onFullDownLoad: handleFullDownloadAttachment,
                      }}
                    />
                  </div>
                  <div className="w-20">
                    <CaptionButton
                      buttonType="basic"
                      caption="ファイル追加"
                      name=""
                      onClick={() => {
                        if (
                          attachmentViewItem &&
                          attachmentViewItem.length >= MAX_ATTACHMENT
                        ) {
                          error([
                            GetMessageWithIntl(intl, {
                              id: 'E0000077',
                              value: { $1: MAX_ATTACHMENT },
                            }),
                          ]);
                          return;
                        }
                        setOpenFileUploadDialog(true);
                      }}
                    />
                  </div>
                </div>
              </div>
            </Accordion>

            {/* 見積依頼先 */}
            <Accordion
              title={GetMessage({ id: 'requestUnit', prefixId: VIEW_ID })}
            >
              <div className="input-line">
                <div className="item-group-100 requestUnit">
                  <div className="w-50">
                    <SimpleListView
                      data={businessUnitContactViewItem}
                      viewOptions={{
                        previewRowCount: 5,
                        keyColumn: 'contactId',
                        columns: businessUnitContactColumns,
                      }}
                      actionOptions={{
                        onDelete: handleDeleteBusinessUnitContact,
                        onDeleteAll: handleAllDeleteBusinessUnitContact,
                      }}
                    />
                  </div>
                  <div className="w-20">
                    <CaptionButton
                      buttonType="basic"
                      caption="追加"
                      name=""
                      onClick={() => {
                        if (
                          businessUnitContactViewItem.length >= MAX_REQUEST_UNIT
                        ) {
                          error([
                            GetMessageWithIntl(intl, {
                              id: 'E0000138',
                              value: { $1: MAX_REQUEST_UNIT },
                            }),
                          ]);
                          return;
                        }
                        setOpenBusinessUnitContactSearchDialog(true);
                      }}
                    />
                  </div>
                </div>
              </div>
            </Accordion>

            {/* 社内管理情報 */}
            <Accordion
              title={GetMessage({ id: 'management', prefixId: VIEW_ID })}
            >
              <div className="input-line">
                <div className="item-group-100">
                  <div className="w-40">
                    <DataFilterbox
                      data={systemNotificationUsersList}
                      name="systemNotificationUsers"
                      labelId="EST_ESTIMATE_REQUEST_INPUT.systemNotificationUsers"
                      columns={preset.columns}
                      searchOption={{ targets: 'displayName' }}
                      onChangeState={setSystemNotificationUsers}
                      value={systemNotificationUsers}
                      multiple={true}
                    />
                  </div>
                </div>
              </div>
              <div className="input-line">
                <div className="item-group-100">
                  <div className="w-80">
                    <Textarea
                      labelId="EST_ESTIMATE_REQUEST_INPUT.remarks"
                      name="remarks"
                      className="w-100 mh-middle"
                      placeholder={GetMessage({
                        id: 'remarks_placeholder',
                        prefixId: VIEW_ID,
                      })}
                      value={remarks}
                      columns={preset.columns}
                      onChangeState={setRemarks}
                    />
                  </div>
                </div>
              </div>
            </Accordion>
          </div>
        </div>
        <div className="footer" ref={footerRef}>
          <div className="footer-contents">
            <div className="input-line">
              <div className="item-group-100">
                <CaptionButton
                  name="saveBtn"
                  caption="一時保存"
                  className="btn"
                  onClick={handleSave}
                  buttonType="basic"
                />
                {/* {承認依頼ボタン} */}
                <CaptionButton
                  name="approvalRequestBtn"
                  caption="承認依頼"
                  className="btn"
                  onClick={() => {
                    // 入力エラーチェック
                    if (isInputErrorCheck()) {
                      return;
                    }
                    // 依頼明細入力(承認依頼時)チェック
                    if (isRequestDetailApprovalRequestErrorCheck()) {
                      return;
                    }
                    setOpenApprovalRequestDialog(true);
                  }}
                  buttonType="basic"
                />
              </div>
            </div>
          </div>
        </div>
        <Toast />
      </Container>
      {/* 確認ダイアログ */}
      <ConfirmationDialog
        isOpen={isOpenConfirmDialog}
        viewMessage={confirmMessage}
        onDecision={handleConfirmed}
        onCancel={handleCancel}
      />
      {/* ファイル追加ダイアログ */}
      <FileUploadWithMemoDialog
        isOpen={isOpenFileUploadDialog}
        uploaderOption={{
          validateOption: {
            maxFileSizeInMebis: 50,
            maxFileCount: MAX_ATTACHMENT,
          },
        }}
        inputOption={{
          categorySystemName: 'B02',
          linkTypeSystemName: 'B01',
        }}
        displayOption={{
          isDnd: true,
          isMultiple: true,
        }}
        messageOption={{
          dialogTitle: {
            prefixId: 'DIALOG_TITLE',
            id: 'file_add',
          },
          buttonType: {
            id: 'decision',
          },
          memoLabelId: 'remarks',
        }}
        onDecision={(result) => {
          handleAddAttachment(result);
          setOpenFileUploadDialog(false);
        }}
        onCancel={() => {
          setOpenFileUploadDialog(false);
        }}
      />
      {/* 取引先(連絡先)検索ダイアログ */}
      <SearchListViewDialog
        viewId="SHARED_CONTACT_CONTENTS_SEARCH"
        fullMethodName={FullMethodName_ListSharedContactContents}
        isOpen={isOpenBusinessUnitContactSearchDialog}
        headerLabelId={{
          prefixId: 'DIALOG_TITLE',
          id: 'sharedContactContentsSearch',
        }}
        onCloseChange={() => {
          clearCheckBox();
          setOpenBusinessUnitContactSearchDialog(false);
        }}
        onSelectChange={(items) =>
          handleSearchSelect(
            items as unknown as mtechnavi.api.company.ISharedContactContent[]
          )
        }
        selectedItemIds={businessUnitContactViewItem.map(
          (contact) => contact.businessUnitContactId
        )}
        menuTarget="businessUnitContact.businessUnitContactId"
        onHandleFormatSchema={onHandleFormatSchema}
        selectLimit={MAX_REQUEST_UNIT}
      />
      {/* 承認依頼ダイアログ */}
      <ApprovalRequestDialog
        isOpen={isOpenApprovalRequestDialog}
        inputOption={{
          organizations: organizations.current,
          organizationRelations: organizationRelations.current,
          FullMethodName_ListUserAttributes: FullMethodName_ListUserAttributes,
          requestReciptId:
            estimateManagementData.current?.estimateManagementId || '',
        }}
        onDecision={(result) => {
          handleApprovalRequest(result);
          setOpenApprovalRequestDialog(false);
        }}
        onCancel={() => {
          setOpenApprovalRequestDialog(false);
        }}
      />

      {isLoading && <LoadingIcon />}
    </>
  );
}
